import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { serviceConsumer } from "../../network/ServiceConsumer";
import Loader from "../../Components/Loader/Loader";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import SingleLineTitle from "../../Components/TitleElements/SingleLineTitle";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DancerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { dancerName } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [dancer, setDancer] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [error, setError] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [editedFname, setEditedFname] = useState("");
  const [editedLname, setEditedLname] = useState("");
  const [editedSchoolId, setEditedSchoolId] = useState("");
  const [editedBirthday, setEditedBirthday] = useState("");
  const [schools, setSchools] = useState([]);

  // Resolve the dancer's school to a real school _id. Prefer the stored
  // school_id; fall back to a unique legacy-name match. Duplicate school names
  // resolve to "" so the admin must pick explicitly (name-based resolution
  // silently picked the first duplicate).
  const resolveSchoolId = (dancerDoc, schoolList) => {
    const stored = dancerDoc?.school_id;
    if (stored) return String(stored._id || stored);
    const name = (dancerDoc?.school || "").trim().toLowerCase();
    if (!name) return "";
    const matches = schoolList.filter(
      (s) => (s.schoolName || "").trim().toLowerCase() === name
    );
    return matches.length === 1 ? String(matches[0]._id) : "";
  };

  useEffect(() => {
    fetchDancerDetails();
    fetchSchools();
  }, [id]);

  // The dancer and school list load in parallel; resolve the select's school
  // id once both are available.
  useEffect(() => {
    if (dancer) setEditedSchoolId(resolveSchoolId(dancer, schools));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dancer, schools]);

  const fetchSchools = async () => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/school?page=1&limit=500&search=&sort=asc`;
      const response = await serviceConsumer("GET", url);
      setSchools(response?.result?.list || []);
    } catch (err) {
      console.log("Failed to load schools");
    }
  };

  const fetchDancerDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/dancer/${id}`;
      const res = await serviceConsumer("GET", url);
      if (res?.dancer) {
        setDancer(res.dancer);
        setEditedFname(res.dancer.fname);
        setEditedLname(res.dancer.lname);
        setEditedBirthday(res.dancer.birthday || "");
      }
      if (res?.registrations) {
        setRegistrations(res.registrations);
      }
    } catch (err) {
      setError("Failed to load dancer details");
      ShowToastMessage("error", "Failed to load dancer details");
    } finally {
      setLoading(false);
    }
  };

  const editClickHandler = () => {
    setEditMode(true);
  };

  const cancelHandler = () => {
    setEditMode(false);
    if (dancer) {
      setEditedFname(dancer.fname);
      setEditedLname(dancer.lname);
      setEditedSchoolId(resolveSchoolId(dancer, schools));
      setEditedBirthday(dancer.birthday || "");
    }
  };

  const saveHandler = async () => {
    // The dropdown binds by school _id (names can duplicate across schools);
    // the server requires a validated school_id reference.
    const schoolSelected = schools.find((s) => String(s._id) === editedSchoolId);
    if (!schoolSelected) {
      ShowToastMessage("error", "Please select a valid school");
      return;
    }
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/dancer/${id}`;
      const payload = {
        fname: editedFname,
        lname: editedLname,
        school: schoolSelected.schoolName,
        school_id: schoolSelected._id,
        birthday: editedBirthday,
      };
      await serviceConsumer("PUT", url, payload);
      ShowToastMessage("success", "Dancer updated successfully");
      setEditMode(false);
      setDancer({ ...dancer, ...payload });
    } catch (err) {
      ShowToastMessage("error", "Something went wrong while saving");
    } finally {
      setLoading(false);
    }
  };

  const backHandler = () => {
    navigate(-1);
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!dancer) return <p className="text-center">No data available</p>;

  return (
    <>
      <SingleLineTitle
        titleName={dancerName || `${dancer.fname} ${dancer.lname}`}
        onBackClick={backHandler}
      />

      <div className="db-body">
        <div className="d-flex align-items-center justify-content-between mb-4 pt-3">
          <h3>{editedFname} {editedLname}</h3>
          <div className="d-flex gap-3">
            {!editMode ? (
              <EditIcon
                style={{ cursor: "pointer", color: "#68ad3f" }}
                titleAccess="Edit Dancer"
                onClick={editClickHandler}
              />
            ) : (
              <>
                <SaveIcon
                  style={{ cursor: "pointer", color: "#68ad3f" }}
                  titleAccess="Save Changes"
                  onClick={saveHandler}
                />
                <CloseIcon
                  style={{ cursor: "pointer", color: "red" }}
                  titleAccess="Cancel"
                  onClick={cancelHandler}
                />
              </>
            )}
          </div>
        </div>

        <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "20px", border: "1px solid #e8e8e8" }}>
          {!editMode ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>First Name</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{dancer.fname}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Last Name</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{dancer.lname}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>School</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{dancer.school || "N/A"}</div>
              </div>
              <div>
                <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Date of Birth</div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>{dancer.birthday || "N/A"}</div>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>First Name</label>
                <input type="text" className="dashboard-input" value={editedFname} onChange={(e) => setEditedFname(e.target.value)} style={{ width: "100%", padding: "6px 10px" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>Last Name</label>
                <input type="text" className="dashboard-input" value={editedLname} onChange={(e) => setEditedLname(e.target.value)} style={{ width: "100%", padding: "6px 10px" }} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>School</label>
                <select className="dashboard-input" value={editedSchoolId} onChange={(e) => setEditedSchoolId(e.target.value)} style={{ width: "100%", padding: "6px 10px" }}>
                  <option value="">Select School</option>
                  {schools.map((school) => (
                    <option key={school._id} value={String(school._id)}>{school.schoolName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "#555", display: "block", marginBottom: "6px", fontWeight: 500 }}>Date of Birth</label>
                <DatePicker
                  className="dashboard-input"
                  selected={editedBirthday ? new Date(editedBirthday, 0, 1) : null}
                  onChange={(date) => setEditedBirthday(date ? date.getFullYear().toString() : "")}
                  dateFormat="yyyy"
                  showYearPicker
                  placeholderText="Select birth year"
                  maxDate={new Date()}
                  minDate={new Date(1900, 0, 1)}
                  isClearable
                  style={{ width: "100%", padding: "6px 10px" }}
                />
              </div>
            </div>
          )}
        </div>

        <h4 className="mt-5">Event Registrations</h4>
        <hr />
        <div className="table-responsive">
          <table className="table my_table">
            <tbody>
              <tr className="header-row">
                <td className="p-25">Event Name</td>
                <td className="p-25-5">Event Date</td>
                <td className="p-25-5">Type</td>
              </tr>
              {registrations.length > 0 ? (
                registrations.map((reg, i) => (
                  <tr key={reg._id || i} style={{ fontSize: "14px" }}>
                    <td>{reg.eventData?.feis_name || "N/A"}</td>
                    <td>
                      {reg.eventData?.feis_date
                        ? moment(reg.eventData.feis_date).format("MMM D, YYYY")
                        : "N/A"}
                    </td>
                    <td>{reg.event_type || "feis"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center">
                    No registrations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default DancerDetails;
